import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import {
  Phone,
  Calendar,
  MapPin,
  Clock,
  Hotel,
  Car,
  Coffee,
  Laptop,
  Heart,
} from "lucide-react";
import { CLINIC_REF, SITE_URL } from "@/components/schemaConstants";
import Breadcrumbs from "@/components/Breadcrumbs";
import { RelatedPages } from "@/components/RelatedPages";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import SaintVideoSection from "@/components/SaintVideoSection";

const SaintTreatmentWeek: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="SAINT® Treatment Week: Day-by-Day Guide | Neuro Wellness"
        description="What a week of SAINT® TMS treatment is like at Neuro Wellness in Coral Springs, FL. Consultation, fMRI, Day 1–5, what happens between sessions, follow-up, and what to bring."
        canonical="/saint-treatment-week"
        keywords="SAINT treatment week, SAINT TMS day by day, what to expect SAINT, SAINT five days, fMRI guided TMS, Coral Springs FL"
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: "What Is a Week of SAINT® TMS Treatment Like?",
            description:
              "A detailed guide to the SAINT® TMS treatment week at Neuro Wellness TMS Centers of America in Coral Springs, Florida.",
            url: `${SITE_URL}/saint-treatment-week`,
            provider: CLINIC_REF,
            about: ["SAINT TMS", "Treatment Week", "Patient Experience"],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "How many days does SAINT TMS treatment take?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "SAINT® TMS is delivered over five consecutive days. Each day includes ten 10-minute treatment sessions with roughly 50-minute breaks between them. Before the treatment week, patients complete a psychiatric consultation and a resting-state fMRI scan.",
                },
              },
              {
                "@type": "Question",
                name: "Can I work during SAINT treatment week?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Many patients work remotely between sessions using a laptop or phone. The 50-minute breaks provide time to check email or take calls. However, the treatment days are full — ten sessions per day — so patients should not plan to work full-time during the treatment week.",
                },
              },
              {
                "@type": "Question",
                name: "Do I need to stay in a hospital for SAINT?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No. SAINT® is an outpatient treatment. Neuro Wellness TMS Centers of America is an outpatient clinic in Coral Springs, Florida. Patients stay at a nearby hotel during the treatment week, not in a hospital. No anesthesia or sedation is used.",
                },
              },
              {
                "@type": "Question",
                name: "What happens between SAINT sessions?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Between sessions, patients relax in a private treatment suite or the SAINT lounge. Most patients read, work on a laptop, rest, or listen to music. The 50-minute breaks are part of the protocol — they allow the brain to respond to stimulation between sessions.",
                },
              },
            ],
          },
        ]}
      />

      <Navbar />

      <main className="pt-24 sm:pt-28">
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/10 via-background to-background py-12 sm:py-20 border-b border-border">
          <div className="container-px mx-auto max-w-6xl px-4 grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div className="max-w-3xl order-2 lg:order-1">
              <Breadcrumbs
                crumbs={[
                  { name: "Home", href: "/" },
                  { name: "SAINT® TMS", href: "/saint" },
                  { name: "Treatment Week", href: "/saint-treatment-week" },
                ]}
              />
              <h1 className="mt-4 font-headings text-3xl font-extrabold text-primary sm:text-4xl lg:text-5xl leading-tight">
                What Is a Week of SAINT® TMS Treatment Like?
              </h1>
              <p className="mt-4 text-lg sm:text-xl text-foreground/80 leading-relaxed">
                SAINT® is delivered over five consecutive days at Neuro Wellness
                TMS Centers of America in Coral Springs, Florida. Each day
                includes ten 10-minute sessions with roughly 50-minute breaks.
                This guide walks through every stage — from consultation and
                fMRI to Day 5 and follow-up.
              </p>
              <div className="mt-6 flex flex-wrap gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-accent text-accent-foreground font-bold shadow-glow"
                >
                  <a href="tel:9548279793">
                    <Phone className="h-5 w-5 mr-2" />
                    Call 954-827-9793
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="#consultation">
                    <Calendar className="h-5 w-5 mr-2" />
                    Schedule a Consultation
                  </a>
                </Button>
              </div>
            </div>
            <div className="w-full order-1 lg:order-2">
              <EligibilityCheckForm
                source="saint-treatment-week"
                redirectTo="/thank-you-saint-google"
              />
            </div>
          </div>
        </section>

        {/* QUICK ANSWER */}
        <section className="py-12 sm:py-16 bg-background">
          <div className="container-px mx-auto max-w-4xl px-4">
            <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-soft">
              <h2 className="font-headings text-xl sm:text-2xl font-bold text-primary mb-3">
                Quick Answer
              </h2>
              <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
                SAINT® TMS is an outpatient treatment delivered over five
                consecutive days at Neuro Wellness TMS Centers of America in
                Coral Springs, Florida. Each day includes ten 10-minute
                treatment sessions with roughly 50-minute breaks between them.
                Before treatment, patients complete a psychiatric consultation
                and a resting-state fMRI scan. Patients stay at a nearby hotel
                during the week — not in a hospital. No anesthesia or sedation
                is used. In clinical trials, 79% of participants reached
                remission, with an average of 2.6 days to remission. Individual
                results vary.
              </p>
            </div>
          </div>
        </section>

        {/* TIMELINE */}
        <section className="py-12 sm:py-16 bg-light-blue/20">
          <div className="container-px mx-auto max-w-4xl px-4">
            <h2 className="font-headings text-2xl sm:text-3xl font-bold text-primary text-center mb-10">
              The SAINT® Treatment Week: Step by Step
            </h2>

            <div className="space-y-8">
              {/* Day 0 */}
              <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-soft">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent font-bold text-sm">
                    0
                  </div>
                  <h3 className="font-headings text-xl font-bold text-primary">
                    Consultation &amp; fMRI
                  </h3>
                </div>
                <p className="text-foreground/85 leading-relaxed">
                  Before the treatment week, patients complete a psychiatric
                  consultation with Dr. Daniel Pietras, MD, or the clinical
                  team. This can be done via Zoom or in person. After the
                  consultation, a resting-state fMRI scan is scheduled. The fMRI
                  takes about 45 minutes and maps brain connectivity to compute
                  the individualized stimulation target. The fMRI must be
                  completed before Day 1.
                </p>
              </div>

              {/* Day 1 */}
              <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-soft">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent font-bold text-sm">
                    1
                  </div>
                  <h3 className="font-headings text-xl font-bold text-primary">
                    Day 1: Starting Treatment
                  </h3>
                </div>
                <p className="text-foreground/85 leading-relaxed mb-3">
                  Patients arrive at the Coral Springs clinic. The treatment
                  team positions the stimulation coil using the fMRI-derived
                  target. The first session establishes the protocol. Over the
                  day, patients complete ten 10-minute sessions with 50-minute
                  breaks.
                </p>
                <p className="text-foreground/85 leading-relaxed">
                  Common sensations include a tapping or knocking feeling on the
                  scalp. Mild headache or scalp discomfort may occur and usually
                  lessens over the first day or two. Patients are awake
                  throughout — no anesthesia.
                </p>
              </div>

              {/* Days 2-4 */}
              <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-soft">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent font-bold text-sm">
                    2–4
                  </div>
                  <h3 className="font-headings text-xl font-bold text-primary">
                    Days 2–4: Continuing the Protocol
                  </h3>
                </div>
                <p className="text-foreground/85 leading-relaxed">
                  Days 2 through 4 follow the same structure: ten sessions per
                  day with 50-minute breaks. The stimulation target remains the
                  same throughout the week. Between sessions, patients read,
                  work, rest, or relax in the private suite or SAINT lounge.
                  Most patients have adjusted to the sensation by Day 2.
                </p>
              </div>

              {/* Day 5 */}
              <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-soft">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent font-bold text-sm">
                    5
                  </div>
                  <h3 className="font-headings text-xl font-bold text-primary">
                    Day 5: Final Treatment Day
                  </h3>
                </div>
                <p className="text-foreground/85 leading-relaxed">
                  Day 5 completes the 50-session protocol. After the final
                  session, the treatment team reviews the week and discusses
                  next steps. Some patients notice mood changes during or
                  shortly after the treatment week. Individual results vary, and
                  a five-day course does not guarantee remission in five days.
                </p>
              </div>

              {/* Follow-up */}
              <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-soft">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent font-bold text-sm">
                    ✓
                  </div>
                  <h3 className="font-headings text-xl font-bold text-primary">
                    After Treatment: Follow-Up
                  </h3>
                </div>
                <p className="text-foreground/85 leading-relaxed">
                  Patients have follow-up appointments with the clinical team.
                  Some follow-up may be remote via Zoom, particularly for
                  traveling patients. A maintenance plan may include medication,
                  psychotherapy, and the possibility of another course if
                  symptoms return. Discuss a maintenance plan with your
                  psychiatrist.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* WHAT TO BRING */}
        <section className="py-12 sm:py-16 bg-background">
          <div className="container-px mx-auto max-w-4xl px-4">
            <h2 className="font-headings text-2xl sm:text-3xl font-bold text-primary text-center mb-8">
              What to Bring for Treatment Week
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Coffee, text: "Snacks and a water bottle" },
                { icon: Laptop, text: "Laptop or tablet for remote work" },
                { icon: Heart, text: "Headphones and calm music" },
                { icon: Clock, text: "Comfortable clothing for sitting" },
                {
                  icon: Car,
                  text: "Current medications in original packaging",
                },
                { icon: MapPin, text: "Insurance card and photo ID" },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 shadow-soft"
                >
                  <item.icon className="h-5 w-5 text-accent shrink-0" />
                  <span className="text-sm sm:text-base text-foreground/85">
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TRAVEL */}
        <section className="py-12 sm:py-16 bg-light-blue/20">
          <div className="container-px mx-auto max-w-4xl px-4">
            <h2 className="font-headings text-2xl sm:text-3xl font-bold text-primary text-center mb-8">
              Travel &amp; Accommodation
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                <Hotel className="h-6 w-6 text-accent mb-3" />
                <h3 className="font-headings text-lg font-bold text-primary mb-2">
                  Nearby Hotel
                </h3>
                <p className="text-sm text-foreground/85 leading-relaxed">
                  Neuro Wellness coordinates nearby hotel accommodations with
                  concierge-level support. Patients typically arrive the day
                  before treatment and stay through Day 5.
                </p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                <Car className="h-6 w-6 text-accent mb-3" />
                <h3 className="font-headings text-lg font-bold text-primary mb-2">
                  Airports &amp; Transportation
                </h3>
                <p className="text-sm text-foreground/85 leading-relaxed">
                  Fort Lauderdale-Hollywood International Airport (FLL) is ~30
                  minutes away. Miami International Airport (MIA) is ~45
                  minutes. A rental car or rideshare is recommended.
                </p>
              </div>
            </div>
            <p className="mt-6 text-center text-sm text-muted-foreground">
              9734 West Sample Road, 2nd Floor, Suite B, Coral Springs, FL 33065
            </p>
          </div>
        </section>

        {/* VIDEO SECTION */}
        <SaintVideoSection variant="full" />

        {/* CTA */}
        <section
          id="consultation"
          className="py-14 sm:py-20 bg-gradient-to-br from-primary via-primary-deep to-primary text-white"
        >
          <div className="container-px mx-auto max-w-5xl px-4 text-center space-y-6">
            <h2 className="font-headings text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white max-w-3xl mx-auto leading-tight">
              Have Questions About the SAINT® Treatment Week?
            </h2>
            <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
              Speak with our clinical team to learn what the five-day treatment
              week would look like for you. Your consultation is free.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button
                asChild
                size="lg"
                className="bg-gradient-accent text-accent-foreground font-bold shadow-glow px-8 py-6 text-base"
              >
                <a href="tel:9548279793">
                  <Phone className="h-5 w-5 mr-2" />
                  Call 954-827-9793
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/40 bg-white/10 text-white hover:bg-white/20 font-semibold px-8 py-6 text-base"
              >
                <a href="/contactus">
                  <Calendar className="h-5 w-5 mr-2" />
                  Schedule a Consultation
                </a>
              </Button>
            </div>
          </div>
        </section>

        <RelatedPages
          heading="Explore SAINT® Resources"
          links={[
            {
              title: "SAINT® Accelerated TMS",
              href: "/saint",
              description: "The 5-day, fMRI-guided protocol",
            },
            {
              title: "What Happens Between SAINT® Sessions",
              href: "/learn/what-happens-between-saint-sessions",
              description: "The 50-minute breaks and the full treatment day",
            },
            {
              title: "What to Expect During SAINT®",
              href: "/learn/what-to-expect-during-saint-tms",
              description: "Patient guide to the five days",
            },
            {
              title: "Travel for SAINT® TMS",
              href: "/learn/travel-for-saint-tms",
              description: "Hotel, airport, and travel planning",
            },
            {
              title: "SAINT® Cost",
              href: "/learn/saint-tms-cost",
              description: "Cost and insurance information",
            },
            {
              title: "Find a Verified SAINT® Provider",
              href: "/find-a-verified-saint-provider",
              description: "Verify our Magnus Medical listing",
            },
          ]}
        />
      </main>

      <Footer />
    </div>
  );
};

export default SaintTreatmentWeek;
